import { GroupMemberRole } from '@group/domain';
import { useCallback, useEffect, useState } from 'react';
import { FullScreenLoading } from '@framework/ui';
import { Loading } from '@framework/ui/atoms/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { UserIcon } from '@framework/ui/atoms/UserIcon';
import { useCurrentUserId } from '@framework/auth/authStatus';
import { KeysRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { WorkspaceId } from 'balus-common';
import type { Workspace } from '@workspace/domain/workspace';
import { WorkspaceMemberDeleteConfirmModal } from './WorkspaceMemberDeleteConfirmModal';
import { useAssignedWorkspaces } from '@workspace/ui/WorkspaceList';
import { NotFound } from '@framework/ui/pages/NotFound';
import { WorkspaceItem } from './WorkspaceItem';
import { classNames } from '@framework/utils';
import type { AppRouterOutputs } from '@framework/trpcClient';
import { trpcClient } from '@framework/trpcClient';
import { ViewModelItem } from './ViewModelItem';
import { useGroupMember } from '@group/hooks/useGroupMembers';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { useTranslation } from 'react-i18next';

type TabTarget = 'AssignedWorkspace' | 'AssignedViewModel';

type AssignedViewModels =
  AppRouterOutputs['viewModel']['individualMember']['listIndividualPermittedViewModels']['results'];

export const GroupConsoleMemberPage: React.FC = () => {
  const { t } = useTranslation();
  const { group } = useGroupConsoleOutletContext();
  const { memberId } = useParams<{ memberId: string }>();
  const [deleteTargetWorkspace, setDeleteTargetWorkspace] = useState<Workspace | null>(null);
  const [internalWorkspaceIds, setInternalWorkspaceIds] = useState<WorkspaceId[]>([]);
  const assignedWorkspaces = useAssignedWorkspaces(memberId ?? null, group.id);
  const currentUserId = useCurrentUserId();

  const memberData = useGroupMember(group.id, memberId ?? '');

  // グループ内公開ワークスペースの一覧取得
  useEffect(() => {
    if (!memberData) return;
    const internalWorkspaceIdsRepo = new KeysRepository(RTDBPath.Group.internalPublicWorkspacesPath(group.id));

    internalWorkspaceIdsRepo.get().then((workspaceIds) => {
      setInternalWorkspaceIds(workspaceIds);
    });
  }, [memberData, group]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteTargetWorkspace(null);
  }, []);

  const [tabTarget, setTabTarget] = useState<TabTarget>('AssignedWorkspace');
  const [assignedViewModels, setAssignedViewModels] = useState<AssignedViewModels | null>(null);

  const fetchListIndividualPermittedViewModels = useCallback(
    () =>
      trpcClient.viewModel.individualMember.listIndividualPermittedViewModels
        .query({
          groupId: group.id,
          userId: memberId ?? '',
        })
        .then(({ results }) => {
          setAssignedViewModels(results);
        }),
    [group.id, memberId]
  );

  useEffect(() => {
    fetchListIndividualPermittedViewModels();
  }, [fetchListIndividualPermittedViewModels]);

  if (!memberId) return <NotFound />;
  if (!memberData) return <FullScreenLoading />;

  // 未ログイン状態ではグループ管理画面を表示できない
  if (!currentUserId) return <NotFound />;

  return (
    <>
      <Link className="mb-4 flex items-center" to={UserPagePathBuilder.groupConsoleMembersPage(group.id)}>
        <FontAwesomeIcon icon={faAngleLeft} className="text-brand" />
        <span className="text-brand">{t('group.members.label')}</span>
      </Link>

      {memberData ? (
        <>
          <div className="mb-4 flex size-10 items-center">
            <UserIcon
              name={memberData.name}
              id={memberData.id}
              imageUrl={memberData.imageUrl}
              isAnonymous={false}
              size="large"
              className="mr-2 grow-0"
            />
            <span className="shrink-0">{memberData.name}</span>
          </div>
          <div className="mb-4">
            <div className="mb-2 font-bold">{t('group.memberPage.emailLabel')}</div>
            <div>{memberData.mailAddress}</div>
          </div>
          <div className="mb-4">
            <div className="mb-2 font-bold">{t('group.members.table.role')}</div>
            <div>
              {memberData.role ? t(GroupMemberRole.getLocalStringKey(memberData.role)) : t('group.memberPage.roleNone')}
            </div>
          </div>

          <div className="mb-2 flex">
            <TabButton
              isActive={tabTarget === 'AssignedWorkspace'}
              handleClick={() => setTabTarget('AssignedWorkspace')}
            >
              <FontAwesomeIcon icon={faUsers} />
              {t('group.members.table.workspaces')}
            </TabButton>
            <TabButton
              isActive={tabTarget === 'AssignedViewModel'}
              handleClick={() => setTabTarget('AssignedViewModel')}
            >
              <FontAwesomeIcon icon={faUser} />
              {t('group.memberPage.tabAssignedViewModel')}
            </TabButton>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100% - 275px)' }}>
            {/* 取得中(null)と0件(空配列)を区別する。区別せずに「未参加」表示を出すと、
                取得完了までの間だけ誤った表示になってしまう。 */}
            {tabTarget === 'AssignedWorkspace' &&
              (!assignedWorkspaces ? (
                <Loading />
              ) : assignedWorkspaces.length <= 0 ? (
                <div>{t('group.memberPage.noWorkspaces')}</div>
              ) : (
                assignedWorkspaces.map((workspace) => (
                  <WorkspaceItem
                    key={workspace.id}
                    workspace={workspace}
                    currentUserId={currentUserId}
                    memberUserEntity={memberData}
                    internalWorkspaceIds={internalWorkspaceIds}
                  />
                ))
              ))}

            {tabTarget === 'AssignedViewModel' &&
              (!assignedViewModels ? (
                <Loading />
              ) : assignedViewModels.length === 0 ? (
                <div>{t('group.memberPage.noViewModels')}</div>
              ) : (
                assignedViewModels.map(({ workspace, viewModel }) => (
                  <ViewModelItem
                    key={viewModel.id}
                    workspace={workspace}
                    viewModel={viewModel}
                    memberUserEntity={memberData}
                    onChanged={() => fetchListIndividualPermittedViewModels()}
                  />
                ))
              ))}
          </div>
        </>
      ) : (
        <div>{t('group.memberPage.noMember')}</div>
      )}
      {deleteTargetWorkspace && memberData && (
        <WorkspaceMemberDeleteConfirmModal
          isOpen={!!deleteTargetWorkspace}
          memberData={memberData}
          workspace={deleteTargetWorkspace}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  );
};

type TabButtonProps = {
  children: React.ReactNode;
  isActive: boolean;
  handleClick: () => void;
};

const TabButton: React.FC<TabButtonProps> = ({ children, isActive, handleClick }: TabButtonProps) => {
  return (
    <button
      className={classNames(
        'flex-1 break-keep border-0 border-b-2 font-bold hover:bg-gray-300 focus:outline-none',
        isActive ? 'border-brand text-brand' : 'border-gray-300'
      )}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};
